import React from 'react';
import { School, Users, GraduationCap, Heart, BookOpen, ShieldCheck, Activity, TrendingUp, AlertTriangle, CheckCircle2, Clock, Server, RefreshCw, Tag } from 'lucide-react';
import { motion } from 'framer-motion';
import { PageLayout } from '../../shared/PageLayout';
import { StatCard } from '../../shared/StatCard';
import { ConnectionBadge } from '../shared/ConnectionBadge';
import { QualityRing, ProgressBar, containerVariants, itemVariants } from './dashboard/DashboardCharts';
import { useDashboardData } from './dashboard/useDashboardData';
export function DashboardPage({
  onNavigate
}: {
  onNavigate?: (view: string) => void;
}) {
  const {
    stats,
    contentStats,
    pipelineLoading,
    strictModeOn,
    handleRefreshPipeline,
    passCount,
    warnCount,
    failCount,
    avgScore,
    totalValidated,
    systemHealth,
    recentActivity
  } = useDashboardData();
  return <PageLayout title="Dashboard" subtitle="Overzicht van het Meester djay platform" actions={<ConnectionBadge status="live" data-id="element-4609" />} data-id="element-4608">
      {/* Section 1: Platform Overview */}
      <motion.div variants={containerVariants} initial="hidden" animate="show" className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4 mb-6" data-id="element-4610">
        <motion.div variants={itemVariants} data-id="element-4611">
          <StatCard label="Leerlingen" value={stats.totalLearners} icon={<Users className="w-4 h-4 text-blue-500" data-id="element-4613" />} data-id="element-4612" />
        </motion.div>
        <motion.div variants={itemVariants} data-id="element-4614">
          <StatCard label="Docenten" value={stats.totalTeachers} icon={<GraduationCap className="w-4 h-4 text-indigo-500" data-id="element-4616" />} data-id="element-4615" />
        </motion.div>
        <motion.div variants={itemVariants} data-id="element-4617">
          <StatCard label="Ouders" value={stats.totalParents} icon={<Heart className="w-4 h-4 text-rose-500" data-id="element-4619" />} data-id="element-4618" />
        </motion.div>
        <motion.div variants={itemVariants} data-id="element-4620">
          <StatCard label="Scholen" value={stats.totalSchools} icon={<School className="w-4 h-4 text-purple-500" data-id="element-4622" />} data-id="element-4621" />
        </motion.div>
      </motion.div>

      {/* Section 2: Content & Quality */}
      <motion.div variants={containerVariants} initial="hidden" animate="show" className="bg-white rounded-xl border border-gray-200 shadow-sm p-4 sm:p-5 mb-6" data-id="element-4623">
        <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-4" data-id="element-4624">
          Content & Kwaliteit
        </h3>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="element-4625">
          {/* Left: Content Stats */}
          <div className="space-y-4" data-id="element-4626">
            <div className="flex items-center justify-between" data-id="element-4627">
              <div className="flex items-center gap-2" data-id="element-4628">
                <BookOpen className="w-4 h-4 text-blue-500" data-id="element-4629" />
                <span className="text-sm font-medium text-gray-700" data-id="element-4630">
                  Vragenbank
                </span>
              </div>
              <span className="text-lg font-bold text-gray-900" data-id="element-4631">
                {contentStats.totalQuestions.toLocaleString('nl')}
              </span>
            </div>
            <div className="flex items-center justify-between" data-id="element-4632">
              <div className="flex items-center gap-2" data-id="element-4633">
                <Tag className="w-4 h-4 text-purple-500" data-id="element-4634" />
                <span className="text-sm font-medium text-gray-700" data-id="element-4635">
                  Families
                </span>
              </div>
              <span className="text-sm text-gray-600" data-id="element-4636">
                {contentStats.totalFamilies} / {contentStats.taxonomyFamilies}
              </span>
            </div>
            <ProgressBar value={contentStats.coveragePct} color={contentStats.coveragePct >= 80 ? 'bg-emerald-500' : contentStats.coveragePct >= 50 ? 'bg-amber-500' : 'bg-red-500'} label="Seed dekking" data-id="element-4637" />
            <ProgressBar value={contentStats.visualCoveragePct} color={contentStats.visualCoveragePct >= 80 ? 'bg-emerald-500' : contentStats.visualCoveragePct >= 50 ? 'bg-amber-500' : 'bg-red-500'} label={`Visuele componenten (${contentStats.usedVisualsCount}/${contentStats.registeredVisuals})`} data-id="element-4638" />
          </div>

          {/* Right: Quality & Validation */}
          <div className="space-y-4" data-id="element-4639">
            <div className="flex items-center justify-between" data-id="element-4640">
              <div className="flex items-center gap-3" data-id="element-4641">
                {avgScore !== null ? <QualityRing score={avgScore} data-id="element-4642" /> : <div className="w-12 h-12 rounded-full bg-gray-100 animate-pulse" data-id="element-4643" />}
                <div data-id="element-4644">
                  <div className="text-sm font-medium text-gray-700" data-id="element-4645">
                    Kwaliteitsscore
                  </div>
                  <div className="text-xs text-gray-500" data-id="element-4646">
                    alle {contentStats.totalQuestions.toLocaleString('nl')}{' '}
                    vragen
                  </div>
                </div>
              </div>
              <button onClick={handleRefreshPipeline} disabled={pipelineLoading} className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-40" title="Validatie vernieuwen" data-id="element-4647">
                <RefreshCw className={`w-4 h-4 ${pipelineLoading ? 'animate-spin' : ''}`} data-id="element-4648" />
              </button>
            </div>

            {pipelineLoading ? <div className="flex gap-4" data-id="element-4649">
                {['Geslaagd', 'Warnings', 'Fouten'].map(l => <div key={l} className="flex-1 bg-gray-50 rounded-lg p-2 animate-pulse" data-id="element-4650">
                    <div className="h-5 bg-gray-200 rounded mb-1" data-id="element-4651" />
                    <div className="h-3 bg-gray-100 rounded w-2/3" data-id="element-4652" />
                  </div>)}
              </div> : passCount !== null ? <>
                <div className="flex gap-3" data-id="element-4653">
                  <div className="flex-1 bg-emerald-50 rounded-lg p-2.5 text-center" data-id="element-4654">
                    <div className="text-lg font-bold text-emerald-700" data-id="element-4655">
                      {passCount}
                    </div>
                    <div className="text-xs text-emerald-600" data-id="element-4656">Geslaagd</div>
                  </div>
                  <div className="flex-1 bg-amber-50 rounded-lg p-2.5 text-center" data-id="element-4657">
                    <div className="text-lg font-bold text-amber-700" data-id="element-4658">
                      {warnCount}
                    </div>
                    <div className="text-xs text-amber-600" data-id="element-4659">Warnings</div>
                  </div>
                  <div className="flex-1 bg-red-50 rounded-lg p-2.5 text-center" data-id="element-4660">
                    <div className="text-lg font-bold text-red-700" data-id="element-4661">
                      {failCount}
                    </div>
                    <div className="text-xs text-red-600" data-id="element-4662">Fouten</div>
                  </div>
                </div>
                <div className="h-2 bg-gray-100 rounded-full overflow-hidden flex" data-id="element-4663">
                  <div className="h-full bg-emerald-500" style={{
                width: `${totalValidated > 0 ? (passCount ?? 0) / totalValidated * 100 : 0}%`
              }} data-id="element-4664" />
                  <div className="h-full bg-amber-400" style={{
                width: `${totalValidated > 0 ? (warnCount ?? 0) / totalValidated * 100 : 0}%`
              }} data-id="element-4665" />
                  <div className="h-full bg-red-500" style={{
                width: `${totalValidated > 0 ? (failCount ?? 0) / totalValidated * 100 : 0}%`
              }} data-id="element-4666" />
                </div>
              </> : <div className="text-sm text-gray-400 text-center py-4" data-id="element-4667">
                Validatie wordt geladen...
              </div>}
          </div>
        </div>
      </motion.div>

      {/* Section 3: Activity & System Health */}
      <motion.div variants={containerVariants} initial="hidden" animate="show" className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6 mb-6" data-id="element-4668">
        {/* Recent Activity */}
        <motion.div variants={itemVariants} className="bg-white rounded-xl border border-gray-200 shadow-sm p-4 sm:p-5" data-id="element-4669">
          <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-4 flex items-center gap-2" data-id="element-4670">
            <Clock className="w-4 h-4" data-id="element-4671" /> Recente Activiteit
          </h3>
          <div className="space-y-3" data-id="element-4672">
            {recentActivity.map((item, i) => <div key={i} className="flex items-start gap-3" data-id="element-4673">
                <div className={`mt-0.5 flex-shrink-0 ${item.type === 'success' ? 'text-emerald-500' : item.type === 'warning' ? 'text-amber-500' : 'text-blue-500'}`} data-id="element-4674">
                  {item.type === 'success' ? <CheckCircle2 className="w-4 h-4" data-id="element-4675" /> : item.type === 'warning' ? <AlertTriangle className="w-4 h-4" data-id="element-4676" /> : <Activity className="w-4 h-4" data-id="element-4677" />}
                </div>
                <div className="min-w-0 flex-1" data-id="element-4678">
                  <p className="text-sm text-gray-700 leading-snug" data-id="element-4679">
                    {item.message}
                  </p>
                  <p className="text-xs text-gray-400 mt-0.5 font-mono" data-id="element-4680">
                    {item.time}
                  </p>
                </div>
              </div>)}
          </div>
        </motion.div>

        {/* System Health */}
        <motion.div variants={itemVariants} className="bg-white rounded-xl border border-gray-200 shadow-sm p-4 sm:p-5" data-id="element-4681">
          <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-4 flex items-center gap-2" data-id="element-4682">
            <Server className="w-4 h-4" data-id="element-4683" /> Systeemgezondheid
          </h3>
          <div className="space-y-4" data-id="element-4684">
            <div className="flex items-center justify-between" data-id="element-4685">
              <span className="text-sm text-gray-600" data-id="element-4686">Strict Mode</span>
              <span className={`text-xs font-bold px-2 py-1 rounded-full ${strictModeOn ? 'bg-amber-100 text-amber-700' : 'bg-gray-100 text-gray-500'}`} data-id="element-4687">
                {strictModeOn ? 'AAN' : 'UIT'}
              </span>
            </div>
            <div className="flex items-center justify-between" data-id="element-4688">
              <span className="text-sm text-gray-600" data-id="element-4689">Gem. responstijd</span>
              <span className="text-sm font-semibold text-gray-800" data-id="element-4690">
                {systemHealth.responseTime}ms
              </span>
            </div>
            <div data-id="element-4691">
              <div className="flex items-center justify-between mb-1.5" data-id="element-4692">
                <span className="text-sm text-gray-600" data-id="element-4693">Opslag</span>
                <span className="text-sm font-semibold text-gray-800" data-id="element-4694">
                  {systemHealth.storageMb} / 5 MB
                </span>
              </div>
              <div className="h-2 bg-gray-100 rounded-full overflow-hidden" data-id="element-4695">
                <div className="h-full bg-violet-500 rounded-full" style={{
                width: `${systemHealth.storagePct}%`
              }} data-id="element-4696" />
              </div>
            </div>
            <div data-id="element-4697">
              <div className="flex items-center justify-between mb-1.5" data-id="element-4698">
                <span className="text-sm text-gray-600" data-id="element-4699">Actief deze week</span>
                <span className="text-sm font-semibold text-gray-800" data-id="element-4700">
                  {stats.activeThisWeek} leerlingen
                </span>
              </div>
              <div className="h-2 bg-gray-100 rounded-full overflow-hidden" data-id="element-4701">
                <div className="h-full bg-blue-500 rounded-full" style={{
                width: `${Math.min(100, stats.activeThisWeek / Math.max(stats.totalLearners, 1) * 100)}%`
              }} data-id="element-4702" />
              </div>
            </div>
          </div>
        </motion.div>
      </motion.div>

      {/* Section 4: Quick Insights */}
      <motion.div variants={containerVariants} initial="hidden" animate="show" className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4" data-id="element-4703">
        {/* Subscriptions */}
        <motion.div variants={itemVariants} className="bg-white rounded-xl border border-gray-200 shadow-sm p-4 sm:p-5" data-id="element-4704">
          <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-4" data-id="element-4705">
            Abonnementen
          </h3>
          <div className="space-y-2.5" data-id="element-4706">
            {stats.planDistribution.map(item => <div key={item.label} data-id="element-4707">
                <div className="flex justify-between text-xs mb-1" data-id="element-4708">
                  <span className="text-gray-600" data-id="element-4709">{item.label}</span>
                  <span className="font-semibold text-gray-800" data-id="element-4710">
                    {item.count}
                  </span>
                </div>
                <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden" data-id="element-4711">
                  <div className={`h-full rounded-full ${item.color}`} style={{
                width: `${item.pct}%`
              }} data-id="element-4712" />
                </div>
              </div>)}
          </div>
        </motion.div>

        {/* Top Learners */}
        <motion.div variants={itemVariants} className="bg-white rounded-xl border border-gray-200 shadow-sm p-4 sm:p-5" data-id="element-4713">
          <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-4 flex items-center gap-1.5" data-id="element-4714">
            <TrendingUp className="w-3.5 h-3.5" data-id="element-4715" /> Top Leerlingen
          </h3>
          {stats.topLearners.length > 0 ? <div className="space-y-2.5" data-id="element-4716">
              {stats.topLearners.map((item, i) => <div key={i} className="flex items-center gap-2.5" data-id="element-4717">
                  <div className="w-6 h-6 rounded-full bg-violet-100 text-violet-700 text-xs font-bold flex items-center justify-center flex-shrink-0" data-id="element-4718">
                    {i + 1}
                  </div>
                  <div className="flex-1 min-w-0" data-id="element-4719">
                    <p className="text-xs font-medium text-gray-800 truncate" data-id="element-4720">
                      {item.name}
                    </p>
                  </div>
                  <span className="text-xs font-bold text-violet-600" data-id="element-4721">
                    {item.xp.toLocaleString('nl-NL')} XP
                  </span>
                </div>)}
            </div> : <p className="text-xs text-gray-400" data-id="element-4722">Geen leerlingen gevonden</p>}
        </motion.div>

        {/* Calibration Progress */}
        <motion.div variants={itemVariants} className="bg-white rounded-xl border border-gray-200 shadow-sm p-4 sm:p-5" data-id="element-4723">
          <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-4 flex items-center gap-1.5" data-id="element-4724">
            <ShieldCheck className="w-3.5 h-3.5" data-id="element-4725" /> Kalibratie
          </h3>
          <div className="space-y-3" data-id="element-4726">
            <div className="flex items-end justify-between" data-id="element-4727">
              <span className="text-2xl font-bold text-gray-900" data-id="element-4728">
                {contentStats.calibration.goldStandardPct}%
              </span>
              <span className="text-xs text-gray-500" data-id="element-4729">
                {contentStats.calibration.familiesAtGoldStandard} /{' '}
                {contentStats.calibration.totalFamilies} goud
              </span>
            </div>
            <div className="h-2 bg-gray-100 rounded-full overflow-hidden" data-id="element-4730">
              <motion.div initial={{
              width: 0
            }} animate={{
              width: `${contentStats.calibration.goldStandardPct}%`
            }} transition={{
              duration: 1,
              delay: 0.3
            }} className={`h-full rounded-full ${contentStats.calibration.goldStandardPct >= 80 ? 'bg-emerald-500' : contentStats.calibration.goldStandardPct >= 40 ? 'bg-amber-500' : 'bg-red-500'}`} data-id="element-4731" />
            </div>
            <div className="grid grid-cols-2 gap-2 text-xs" data-id="element-4732">
              <div className="flex justify-between" data-id="element-4733">
                <span className="text-gray-500" data-id="element-4734">citoDifficulty</span>
                <span className="font-semibold text-gray-700" data-id="element-4735">
                  {contentStats.calibration.citoDifficultyCoveragePct}%
                </span>
              </div>
              <div className="flex justify-between" data-id="element-4736">
                <span className="text-gray-500" data-id="element-4737">solutionSteps</span>
                <span className="font-semibold text-gray-700" data-id="element-4738">
                  {contentStats.calibration.solutionStepsCoveragePct}%
                </span>
              </div>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </PageLayout>;
}